import { ReactNode } from "react";
import { FaFacebookF, FaInstagram, FaYoutube, FaWhatsapp, FaGooglePlay, FaApple } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { cn } from "@/lib/utils";

// Arattai icon component using actual logo image
const ArattaiIcon = ({ size = 16, src }: { size?: number; src?: string }) => (
  <img
    src={src || "https://media.dinamani.com/dinamani/2026-02-25/gjt6ao2j/Arattailogo.png"}
    alt="Arattai"
    width={size}
    height={size}
    className="rounded-sm"
    style={{ objectFit: 'contain' }}
  />
);

export interface SocialLinkConfig {
  id: string;
  platform: string;
  url: string;
  iconUrl?: string;
}

type SocialIconProps = {
  className?: string;
  useGrid?: boolean;
  showAppIcons?: boolean;
  links?: SocialLinkConfig[];
};

interface IconRegistryEntry {
  icon: (color: string) => ReactNode;
  bgColor: string;
  isImage?: boolean;
}

// Known platforms get a brand icon + color; unrecognized platforms fall back
// to iconUrl (rendered as an <img>) or are skipped rather than rendering broken.
const ICON_REGISTRY: Record<string, IconRegistryEntry> = {
  facebook: { icon: (color) => <FaFacebookF size={16} color={color} />, bgColor: "#3B5998" },
  arattai: { icon: () => <ArattaiIcon size={24} />, bgColor: "#FFC107", isImage: true },
  instagram: { icon: (color) => <FaInstagram size={16} color={color} />, bgColor: "#D62976" },
  x: { icon: (color) => <FaXTwitter size={16} color={color} />, bgColor: "#000000" },
  twitter: { icon: (color) => <FaXTwitter size={16} color={color} />, bgColor: "#000000" },
  youtube: { icon: (color) => <FaYoutube size={16} color={color} />, bgColor: "#FF0200" },
  whatsapp: { icon: (color) => <FaWhatsapp size={16} color={color} />, bgColor: "#67C15E" },
  playstore: { icon: (color) => <FaGooglePlay size={16} color={color} />, bgColor: "#A4C639" },
  apple: { icon: (color) => <FaApple size={16} color={color} />, bgColor: "#000000" },
};

const normalizePlatform = (platform: string) => platform.toLowerCase().replace(/[^a-z]/g, "");

// Fallback set used when no `links` config is provided (e.g. Storybook usage
// or before the header-footer config has synced).
const DEFAULT_LINKS: SocialLinkConfig[] = [
  { id: "facebook", platform: "Facebook", url: "https://www.facebook.com/DinamaniDaily" },
  { id: "arattai", platform: "Arattai", url: "https://aratt.ai/@dinamani" },
  { id: "instagram", platform: "Instagram", url: "https://www.instagram.com/dinamanidaily/" },
  { id: "x", platform: "X", url: "https://x.com/DinamaniDaily" },
  { id: "youtube", platform: "YouTube", url: "https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w" },
  { id: "whatsapp", platform: "WhatsApp", url: "https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G" },
  { id: "playstore", platform: "Play Store", url: "https://play.google.com/store/apps/details?id=com.dinamani.android" },
  { id: "apple", platform: "Apple", url: "https://apps.apple.com/app/dinamani-tamil-news/id1244532821" },
];

const SocialIcons = ({ className, useGrid = false, showAppIcons = true, links }: SocialIconProps) => {
  const source = links ?? (showAppIcons ? DEFAULT_LINKS : DEFAULT_LINKS.filter(l => l.platform !== "Play Store" && l.platform !== "Apple"));

  const resolved = source
    .map((link) => {
      const registryEntry = ICON_REGISTRY[normalizePlatform(link.platform)];
      const color = useGrid ? (registryEntry?.bgColor ?? "#1B1B1B") : "#FFFFFF";

      if (registryEntry) {
        return {
          key: link.id,
          href: link.url,
          label: link.platform,
          bgColor: registryEntry.bgColor,
          isImage: !!registryEntry.isImage,
          icon: registryEntry.isImage ? <ArattaiIcon size={24} src={link.iconUrl} /> : registryEntry.icon(color),
        };
      }

      if (link.iconUrl) {
        return {
          key: link.id,
          href: link.url,
          label: link.platform,
          bgColor: "#808080",
          isImage: true,
          icon: <img src={link.iconUrl} alt={link.platform} width={16} height={16} className="object-contain" />,
        };
      }

      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <ul className={cn(
      useGrid
        ? "flex flex-wrap items-center gap-3"
        : "flex flex-wrap items-center gap-3",
      className
    )}>
      {resolved.map((item) => (
        <li key={item.key}>
          <a
            href={item.href}
            target="_blank"
            rel="noreferrer"
            aria-label={item.label}
            className={cn(
              "inline-flex items-center justify-center shadow-sm hover:shadow transition-all",
              item.isImage
                ? "w-6 h-6 rounded-md overflow-hidden"
                : `w-6 h-6 rounded-full ${useGrid ? 'bg-FFFFFF border border-DCDCDC' : ''}`
            )}
            style={!useGrid ? { backgroundColor: item.bgColor } : {}}
          >
            {item.icon}
          </a>
        </li>
      ))}
    </ul>
  );
};

export default SocialIcons;
